import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';

export interface PicklistOption {
  value: string;
  label: string;
}

interface PicklistProps {
  name: string;
  label: string;
  value: string;
  options: PicklistOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function Picklist({
  name,
  label,
  value,
  options,
  onChange,
  disabled = false,
  readOnly = false,
  required = false,
  error = false,
  helperText = '',
}: PicklistProps) {
  const { t } = useTranslation();
  const labelRef = useRef<HTMLLabelElement | null>(null);
  const selectContainerRef = useRef<HTMLDivElement | null>(null);
  const previousValueRef = useRef(value);
  const [unshrunkLineCount, setUnshrunkLineCount] = useState(1);
  const [shrunkLineCount, setShrunkLineCount] = useState(1);
  const [lineHeightPx, setLineHeightPx] = useState(0);
  const [focused, setFocused] = useState(false);
  const [expandingLabel, setExpandingLabel] = useState(false);

  const displayLabel = label;
  const isShrunk = Boolean(value) || focused;

  const allowedValues = useMemo(
    () => new Set(options.map((option) => option.value)),
    [options]
  );

  useEffect(() => {
    if (!value || allowedValues.has(value)) return;
    onChange('');
  }, [allowedValues, onChange, value]);

  const measureLabels = () => {
    const labelElement = labelRef.current;
    if (!labelElement || typeof window === 'undefined') return;

    const computedStyle = window.getComputedStyle(labelElement);
    const computedLineHeight = parseInt(computedStyle.lineHeight, 10) || 18;
    setLineHeightPx(computedLineHeight);

    const container = selectContainerRef.current?.closest('.MuiFormControl-root');
    const containerWidth = container ? container.getBoundingClientRect().width : 300;
    const inputWidth = Math.max(1, containerWidth - 45);

    const unshrunkDiv = document.createElement('div');
    Object.assign(unshrunkDiv.style, {
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      whiteSpace: 'normal',
      width: `${inputWidth}px`,
      font: computedStyle.font,
      lineHeight: computedStyle.lineHeight,
      padding: '0',
      margin: '0',
      border: '0',
    });
    unshrunkDiv.textContent = labelElement.textContent;
    document.body.appendChild(unshrunkDiv);
    const unshrunkHeight = unshrunkDiv.getBoundingClientRect().height;
    setUnshrunkLineCount(Math.max(1, Math.ceil(unshrunkHeight / computedLineHeight)));
    document.body.removeChild(unshrunkDiv);

    const shrunkLabelRect = labelElement.getBoundingClientRect();
    const shrunkWidth = Math.max(1, shrunkLabelRect.width);
    const fontSizePx = parseFloat(computedStyle.fontSize);

    const shrunkDiv = document.createElement('div');
    Object.assign(shrunkDiv.style, {
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      whiteSpace: 'normal',
      width: `${shrunkWidth}px`,
      padding: '0',
      margin: '0',
      border: '0',
      fontFamily: computedStyle.fontFamily,
      fontWeight: computedStyle.fontWeight,
      fontSize: `${fontSizePx * 0.75}px`,
      lineHeight: `${computedLineHeight * 0.75}px`,
    });
    shrunkDiv.textContent = labelElement.textContent;
    document.body.appendChild(shrunkDiv);
    const shrunkHeight = shrunkDiv.getBoundingClientRect().height;
    setShrunkLineCount(Math.max(1, Math.ceil(shrunkHeight / (computedLineHeight * 0.75))));
    document.body.removeChild(shrunkDiv);
  };

  useEffect(() => {
    const timer = window.setTimeout(measureLabels, 100);
    window.addEventListener('resize', measureLabels);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', measureLabels);
    };
  }, [displayLabel]);

  useEffect(() => {
    const hadValueBefore = Boolean(previousValueRef.current);
    const hasValueNow = Boolean(value);
    if (hadValueBefore && !hasValueNow) setExpandingLabel(true);
    previousValueRef.current = value;

    if (hadValueBefore !== hasValueNow) {
      window.setTimeout(() => {
        measureLabels();
        window.setTimeout(() => {
          setExpandingLabel(false);
        }, hasValueNow ? 200 : 350);
      }, 50);
    }
  }, [value]);

  const effectivePadding = (() => {
    if (expandingLabel && unshrunkLineCount > 1) return (unshrunkLineCount - 1) * lineHeightPx;
    if (!isShrunk && unshrunkLineCount > 1) return (unshrunkLineCount - 1) * lineHeightPx;
    if (isShrunk && shrunkLineCount > 1) return (shrunkLineCount - 1) * lineHeightPx * 0.75;
    return 0;
  })();

  const inputHeight = 56 + effectivePadding;
  const transitionDuration = '200ms';
  const transitionEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';

  const handleChange = (event: SelectChangeEvent<string>) => {
    if (readOnly) return;
    const nextValue = event.target.value;
    if (nextValue === '' && value !== '') setExpandingLabel(true);
    onChange(nextValue);
    window.setTimeout(() => {
      if (nextValue !== '') setExpandingLabel(false);
    }, nextValue !== '' ? 200 : 300);
  };

  return (
    <FormControl
      fullWidth
      variant="outlined"
      required={required}
      error={error}
      sx={{
        position: 'relative',
        transition: `all ${transitionDuration} ${transitionEasing}`,
        '& .MuiInputLabel-root': {
          paddingRight: '8px',
          whiteSpace: 'normal',
          zIndex: 1,
          transition: `transform ${transitionDuration} ${transitionEasing} !important`,
        },
        '& .MuiOutlinedInput-root': {
          minHeight: `${inputHeight}px`,
          paddingTop: effectivePadding > 0 ? `${effectivePadding}px` : undefined,
          transition: `all ${transitionDuration} ${transitionEasing}`,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          transition: `all ${transitionDuration} ${transitionEasing}`,
          zIndex: 0,
        },
        '& .MuiSelect-select': {
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          lineHeight: 1.35,
        },
      }}
    >
      <InputLabel ref={labelRef} id={`${name}-label`} shrink={isShrunk}>
        {displayLabel}
      </InputLabel>
      <Select
        ref={selectContainerRef}
        labelId={`${name}-label`}
        id={name}
        name={name}
        value={value}
        label={displayLabel}
        onChange={handleChange}
        disabled={disabled}
        variant="outlined"
        onOpen={readOnly ? () => false : () => setFocused(true)}
        onClose={readOnly ? undefined : () => setFocused(false)}
        MenuProps={{
          PaperProps: {
            sx: {
              maxWidth: 400,
            },
          },
        }}
        sx={readOnly ? {
          '& .MuiSelect-select': {
            cursor: 'default !important',
            backgroundColor: '#f5f5f5',
            pointerEvents: 'none',
          },
          '& .MuiSelect-icon': { display: 'none' },
          '& .MuiOutlinedInput-root': {
            cursor: 'default',
            backgroundColor: '#f5f5f5',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
            },
          },
        } : undefined}
      >
        <MenuItem value="">
          <em>{t('SelectAnOption', 'Select an option')}</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              minHeight: 'auto',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
